import { DASH_COLOURS, type DashCard, type DashColour, dashCardPoints, isDashWild } from './play.js';
import type { ColourDashView } from './view.js';

/** `easy` plays any legal card and names a random colour. `clever` hits whenever it can, saves its wilds, and keeps its strongest colour. */
export type ColourDashRobotLevel = 'easy' | 'clever';

export type ColourDashAction =
  | { readonly type: 'play'; readonly card: DashCard; readonly colour?: DashColour }
  | { readonly type: 'draw' }
  | { readonly type: 'pass' }
  | { readonly type: 'call-last' };

/** How many of each colour a hand holds (wilds count for none). */
function colourCounts(hand: readonly DashCard[]): Record<DashColour, number> {
  const counts: Record<DashColour, number> = { red: 0, yellow: 0, green: 0, blue: 0 };
  for (const c of hand) if (c.colour) counts[c.colour]++;
  return counts;
}

/** The colour a hand holds most of (ties: the first in colour order); `fallback` when it holds none. */
function bestColour(hand: readonly DashCard[], fallback: DashColour): DashColour {
  const counts = colourCounts(hand);
  const best = DASH_COLOURS.reduce((a, b) => (counts[b] > counts[a] ? b : a), DASH_COLOURS[0] as DashColour);
  return counts[best] > 0 ? best : fallback;
}

/**
 * What a robot does on its turn: it sees only its own `ColourDashView`. Both levels call "last card" when they hold two. `random` is used by the
 * easy robot only, so a clever game is fully deterministic.
 */
export function colourDashChoose(view: ColourDashView, level: ColourDashRobotLevel, random: () => number): ColourDashAction {
  if (!view.canAct) throw new Error("colourDashChoose: not this seat's turn");
  if (view.canCallLast && view.legalPlays.length > 0) return { type: 'call-last' };
  const legal = view.legalPlays;
  if (legal.length === 0) return view.canDraw ? { type: 'draw' } : { type: 'pass' };

  const play = (card: DashCard): ColourDashAction => {
    if (!isDashWild(card)) return { type: 'play', card };
    const rest = view.hand.filter((c) => c.id !== card.id);
    const colour = level === 'easy' ? (DASH_COLOURS[Math.floor(random() * DASH_COLOURS.length)] as DashColour) : bestColour(rest, view.colour);
    return { type: 'play', card, colour };
  };
  if (level === 'easy') return play(legal[Math.floor(random() * legal.length)] as DashCard);

  // Clever. Every hit (Skip, Draw Two) slows the table down and costs the next player a turn, so play one whenever it fits; a Wild Draw Four,
  // being a wild, is kept for a player who is one or two cards from going out (or for when nothing else fits).
  const nextCards = view.handSizes[view.nextSeat] ?? 7;
  const threatened = nextCards <= 2;
  const hits = (c: DashCard) => c.kind === 'skip' || c.kind === 'draw-two' || (c.kind === 'reverse' && view.seats.length === 2);
  const hitList = legal.filter((c) => hits(c) || (threatened && c.kind === 'wild-draw-four')).sort((a, b) => hitPower(b) - hitPower(a));
  if (hitList.length > 0) return play(hitList[0] as DashCard);
  // Otherwise save the wilds: play the highest-scoring plain card, preferring one that leaves the colour in force a colour it holds plenty of.
  const plain = legal.filter((c) => !isDashWild(c));
  const pool = plain.length > 0 ? plain : legal;
  const counts = colourCounts(view.hand);
  const score = (c: DashCard) => dashCardPoints(c) + (c.colour ? counts[c.colour] * 3 : 0);
  const card = pool.reduce((a, b) => (score(b) > score(a) ? b : a));
  return play(card);
}

/** How much a hitting card hurts the player after you. */
const hitPower = (c: DashCard): number => (c.kind === 'wild-draw-four' ? 4 : c.kind === 'draw-two' ? 3 : 2);
